/**
 * What a recovery run ACTUALLY covers, when the server covers less than the operator confirmed
 * (codex blocking at 2b7706c83, both legs).
 *
 * ⛔⛔ THE DASHBOARD ASKED THE OPERATOR TO AGREE TO A NUMBER AND THEN IGNORED THE ONE THE SERVER
 * CAME BACK WITH. `POST /suggest` caps a stranded-provenance recovery run at RECOVERY_MAX_LIMIT
 * and reports `limitClamped` / `effectiveLimit` / `requestedLimit`. `handleSuggest` read none of
 * them, so a confirmation for 34,812 gaps started a job touching 20,000 that then finished looking
 * complete — with no indication another batch was needed.
 * 🔑 THE COUNT AND THE WORK MUST NOT DIVERGE. This PR has produced that divergence on three axes
 *   already (population, bound, filters); this was the fourth, sitting in the one place where the
 *   operator had just been asked to agree to a number.
 *
 * ⚠️ EXTRACTED SO IT CAN BE EXECUTED. Inline in a 1,200-line component the only available check was
 * a regex over the source, which proves a `limitClamped` reference exists and nothing about which
 * number reaches the screen — and getting the number wrong here is the entire defect.
 * ⚠️ IT READS THE RESPONSE, NEVER A LOCAL COPY OF THE CEILING. Duplicating the backend's 20,000 in
 *   the client would be a second source of truth that drifts silently the day it changes.
 */

export interface SuggestResponse {
  limitClamped?: boolean;
  effectiveLimit?: number;
  requestedLimit?: number;
  /** What the server's plan says this run will actually cover. */
  willProcess?: number;
  /** How many matching rows this run will NOT reach. */
  remaining?: number;
}

/**
 * @param data       the /suggest response body
 * @param confirmed  the number the operator agreed to (the plan's willProcess)
 * @returns the sentence to show the operator, or null when the run covers everything confirmed
 */
export function describeClampedBatch(
  data: SuggestResponse | null | undefined,
  confirmed?: number,
): string | null {
  if (!data) return null;

  /**
   * ⛔⛔ THE POPULATION CAN MOVE BETWEEN THE PLAN AND THE RUN. The confirm is built from
   * `/suggest/plan`; `/suggest` re-plans, because trusting a client-supplied plan would be the
   * affordance-is-not-authorization defect one door over. Almost always the two agree — but if
   * another operator approved fifty gaps in between, the run covers a different number than the
   * one just agreed to, and THAT is the divergence this whole change is about.
   * 🔑 A RE-PLAN THAT NOBODY COMPARES IS THE SAME AS NO PLAN.
   */
  if (typeof data.willProcess === 'number' && typeof confirmed === 'number'
      && Number.isFinite(data.willProcess) && data.willProcess !== confirmed) {
    const left = typeof data.remaining === 'number' && data.remaining > 0
      ? ` ${data.remaining.toLocaleString()} will remain afterwards.`
      : '';
    return `This run covers ${data.willProcess.toLocaleString()} gaps, not the `
      + `${confirmed.toLocaleString()} shown a moment ago — the queue changed between the two. `
      + `Nothing is lost: press the button again when it finishes to pick up the rest.${left}`;
  }

  if (data.limitClamped !== true) return null;
  // ⛔ A clamp we cannot size is still worth saying out loud. Silence here would be the original
  //    defect exactly: the run does less than was confirmed and nothing says so.
  if (typeof data.effectiveLimit !== 'number' || !Number.isFinite(data.effectiveLimit)) {
    return 'This run covers fewer gaps than you confirmed — the server capped the batch but did not '
      + 'report its size. Press the button again once this job finishes to continue.';
  }

  const requested = typeof data.requestedLimit === 'number' && Number.isFinite(data.requestedLimit)
    ? data.requestedLimit
    : (typeof confirmed === 'number' && Number.isFinite(confirmed) ? confirmed : null);
  const remaining = requested !== null ? requested - data.effectiveLimit : null;

  const head = `This run covers ${data.effectiveLimit.toLocaleString()} gaps — the most affected ones — not the `
    + `${requested !== null ? requested.toLocaleString() : 'full number'} you confirmed.`;

  // ⇒ `remaining > 0` and not just `remaining !== null`: a server that clamps to exactly what was
  //   asked for leaves nothing outstanding, and promising a second run there would be its own lie.
  const tail = remaining !== null && remaining > 0
    ? ` ${remaining.toLocaleString()} still need a further run: press the button again once this job finishes.`
    : ' Press the button again once this job finishes to continue.';

  return head + tail;
}
